import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown, FileSpreadsheet, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { useTemplateLibraryAccess } from "@/hooks/use-template-library-access";
import { FormInput } from "@/components/forms/input";
import {
  useCreateBusinessReportMutation,
  useUpdateBusinessReportMutation,
  useGetTemplateCategoriesQuery,
  useCreateTemplateCategoryMutation,
  useReplaceBusinessReportPromptsMutation,
} from "../../businessReports.api";
import {
  BusinessReport,
  BusinessReportType,
  TemplateVisibility,
  isAuthoringTemplateType,
} from "@/modules/snapshots/businessReports.types";
import { AuthoringChecksEditor } from "@/modules/snapshots/components/AuthoringChecksEditor";
import { EMPTY_CHECK, cleanChecks, type CheckDraft } from "@/modules/snapshots/components/authoringChecks";
import { toast } from "react-toastify";
import { CategorySelector, NO_CATEGORY_VALUE } from "./CategorySelector";

interface Props {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  /** Edit this template (name, description, and for authoring templates the checks). */
  businessReport?: BusinessReport;
  /** Start a new template as a copy of this one: "Copy of <name>", its prompts as checks, private. */
  duplicateFrom?: BusinessReport;
  type?: BusinessReportType;
}

interface FormInputs {
  name: string;
  description: string;
  file?: FileList;
  category_id: string;
  type: BusinessReportType;
  visibility: TemplateVisibility;
}

const CREATE_NEW_CATEGORY_VALUE = "__create_new__";
const NAME_CONFLICT_STATUS = 409;

const TYPE_OPTIONS: { value: BusinessReportType; label: string }[] = [
  { value: "snapshot_template", label: "SnapShot Template" },
  { value: "business_report", label: "Business Report" },
  { value: "authoring_template", label: "Authoring Template" },
];

const VISIBILITY_OPTIONS: { value: TemplateVisibility; label: string }[] = [
  { value: "private", label: "Only me" },
  { value: "organization", label: "Everyone in the organization" },
];

/** A template's stored prompts as editor rows (prompt name = heading), in order; `keepIds` for editing in place. */
const checksOf = (source: BusinessReport | undefined, keepIds: boolean): CheckDraft[] =>
  source && source.prompts.length > 0
    ? [...source.prompts]
        .sort((first, second) => first.order - second.order)
        .map(prompt => ({ ...(keepIds ? { id: prompt.id } : {}), heading: prompt.name, prompt: prompt.prompt }))
    : [{ ...EMPTY_CHECK }];

const conflictDetail = (error: unknown): string | null => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response ?? error;
  const { status, data } = (response ?? {}) as { status?: number; data?: { detail?: unknown } };
  return status === NAME_CONFLICT_STATUS && typeof data?.detail === "string" ? data.detail : null;
};

const SelectField = <T extends string>({
  ariaLabel,
  label,
  options,
  value,
  onChange,
  hint,
}: {
  ariaLabel: string;
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  hint?: React.ReactNode;
}) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-[#344054]">{label}</label>
    <Select.Root value={value} onValueChange={next => onChange(next as T)}>
      <Select.Trigger
        className="inline-flex h-11 w-full items-center justify-between gap-2 rounded-md border border-[#ddd5cb] bg-[#fffdfb] px-4 text-sm font-medium text-[#344054] outline-none hover:bg-white focus:ring-2 focus:ring-[#d1974f]"
        aria-label={ariaLabel}
      >
        <Select.Value />
        <Select.Icon>
          <ChevronDown className="h-4 w-4 text-[#687386]" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-[#ddd5cb] bg-[#fffdfb] shadow-lg"
        >
          <Select.Viewport className="p-2">
            {options.map(option => (
              <Select.Item
                key={option.value}
                value={option.value}
                className="relative flex cursor-pointer select-none items-center rounded-md px-8 py-2 text-sm text-[#344054] outline-none data-[highlighted]:bg-[#f4eadc] data-[highlighted]:text-[#07142d]"
              >
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator className="absolute left-2 inline-flex items-center text-[#c88b43]">
                  <Check className="h-4 w-4" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
    {hint && <p className="text-xs font-medium text-[#687386]">{hint}</p>}
  </div>
);

export const CreateBusinessReportModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  businessReport,
  duplicateFrom,
  type = "snapshot_template",
}) => {
  const { isAdmin } = useTemplateLibraryAccess();
  // Authoring users (no admin role) only ever create private authoring templates.
  const initialType: BusinessReportType =
    businessReport?.type ?? (isAdmin && !duplicateFrom ? type : "authoring_template");
  const initialVisibility: TemplateVisibility =
    businessReport?.visibility ?? (isAdmin && !duplicateFrom ? "organization" : "private");
  const initialValues = React.useCallback(
    (): FormInputs => ({
      name: businessReport?.name ?? (duplicateFrom ? `Copy of ${duplicateFrom.name}` : ""),
      description: businessReport?.description ?? duplicateFrom?.description ?? "",
      category_id: businessReport?.category_id ?? "",
      type: initialType,
      visibility: initialVisibility,
    }),
    [businessReport, duplicateFrom, initialType, initialVisibility],
  );
  const methods = useForm<FormInputs>({ defaultValues: initialValues() });

  const effectiveType = methods.watch("type");
  const effectiveVisibility = methods.watch("visibility");
  const isBusinessReport = effectiveType === "business_report";
  const isAuthoring = isAuthoringTemplateType(effectiveType);
  const labelNoun = isBusinessReport ? "Business Report" : isAuthoring ? "Authoring template" : "SnapShot template";
  const [checks, setChecks] = React.useState<CheckDraft[]>(() =>
    checksOf(businessReport ?? duplicateFrom, businessReport !== undefined),
  );

  const [createBusinessReport] = useCreateBusinessReportMutation();
  const [updateBusinessReport] = useUpdateBusinessReportMutation();
  const [replacePrompts] = useReplaceBusinessReportPromptsMutation();
  const [createCategory] = useCreateTemplateCategoryMutation();
  const { data: categories = [] } = useGetTemplateCategoriesQuery(undefined, { skip: !isAdmin });

  const [isCreatingCategory, setIsCreatingCategory] = React.useState(false);
  const [newCategoryName, setNewCategoryName] = React.useState("");

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<FormInputs>();

  // Reset only when the dialog opens (or opens for another template): a background refetch of the
  // library must not wipe rows the user is still editing.
  const latestInitial = React.useRef({ values: initialValues, businessReport, duplicateFrom });
  latestInitial.current = { values: initialValues, businessReport, duplicateFrom };
  const businessReportId = businessReport?.id;
  const duplicateFromId = duplicateFrom?.id;
  React.useEffect(() => {
    if (isOpen) {
      const current = latestInitial.current;
      methods.reset(current.values());
      setChecks(checksOf(current.businessReport ?? current.duplicateFrom, current.businessReport !== undefined));
      setIsCreatingCategory(false);
      setNewCategoryName("");
    }
  }, [isOpen, methods, businessReportId, duplicateFromId]);

  const handleCategoryChange = (value: string) => {
    if (value === CREATE_NEW_CATEGORY_VALUE) {
      setIsCreatingCategory(true);
    } else if (value === NO_CATEGORY_VALUE) {
      methods.setValue("category_id", "");
    } else {
      methods.setValue("category_id", value);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const result = await createCategory({ name: newCategoryName.trim() }).unwrap();
      methods.setValue("category_id", result.id);
      setIsCreatingCategory(false);
      setNewCategoryName("");
    } catch (error) {
      const apiError = error as { data?: { detail?: string } };
      toast.error(apiError?.data?.detail ?? "Failed to create category");
    }
  };

  const onSubmit = async (payload: FormInputs) => {
    const cleanedChecks = isAuthoring ? cleanChecks(checks) : [];
    if (isAuthoring && cleanedChecks.length === 0) {
      toast.error("Write at least one check with a heading and a prompt, or upload a file.");
      return;
    }
    try {
      const categoryId = payload.category_id || null;
      const base = { name: payload.name, description: payload.description };
      // Only admins may set type, category or visibility; an owner's PATCH carries name/description.
      const adminFields = isAdmin
        ? {
            ...(categoryId || businessReport ? { category_id: categoryId } : {}),
            type: payload.type,
            ...(isAuthoring ? { visibility: payload.visibility } : {}),
          }
        : {};

      if (!businessReport) {
        await createBusinessReport({
          data: {
            ...base,
            ...adminFields,
            ...(isAdmin ? {} : { type: "authoring_template" as const, visibility: "private" as const }),
            ...(isAuthoring
              ? { prompts: JSON.stringify(cleanedChecks.map(check => ({ name: check.heading, prompt: check.prompt }))) }
              : payload.file
                ? { file: payload.file }
                : {}),
          },
        }).unwrap();
      } else {
        await updateBusinessReport({ data: { ...base, ...adminFields }, id: businessReport.id }).unwrap();
        if (isAuthoring) {
          await replacePrompts({
            id: businessReport.id,
            prompts: cleanedChecks.map(check => ({ id: check.id, name: check.heading, prompt: check.prompt })),
          }).unwrap();
        }
      }

      setIsOpen(false);
      toast.success(businessReport ? `${labelNoun} updated` : `${labelNoun} created`);
    } catch (error) {
      const conflict = conflictDetail(error);
      if (conflict) {
        methods.setError("name", { type: "server", message: conflict });
        return;
      }
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  const title = businessReport
    ? `Edit ${labelNoun}`
    : duplicateFrom
      ? `Duplicate ${labelNoun}`
      : isAdmin
        ? `Upload ${labelNoun}`
        : `New ${labelNoun}`;

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-[88vh] max-w-3xl gap-0 overflow-y-auto border-[#ddd5cb] bg-[#fbf8f4] p-0 shadow-[0_18px_50px_rgba(15,23,42,0.14)] sm:rounded-lg">
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader className="border-b border-[#ebe5de] bg-[#fffdfb] px-7 py-6">
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#f3e4cf] text-[#c88b43]">
                  <FileSpreadsheet className="h-6 w-6" />
                </span>
                <div className="space-y-1">
                  <DialogTitle className="text-2xl font-semibold leading-tight text-[#07142d]">{title}</DialogTitle>
                  <p className="text-sm font-medium text-[#687386]">
                    {isAuthoring
                      ? isAdmin
                        ? "Name the template and write the checks Pathfinder runs over a document as tracked changes."
                        : "A private template only you can see and run. Write the checks Pathfinder runs over a document as tracked changes."
                      : "Configure the template metadata and upload the source spreadsheet used for snapshot generation."}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="grid gap-5 px-7 py-6">
              <div className={isAdmin ? "grid gap-5 sm:grid-cols-2" : "grid gap-5"}>
                <label className="space-y-2">
                  <span className="text-sm font-semibold text-[#344054]">Template name</span>
                  <FormInput
                    name="name"
                    placeholder="Enter name"
                    className="h-11 rounded-md border-[#ddd5cb] bg-[#fffdfb] text-sm shadow-none placeholder:text-[#9aa4b2] focus-visible:ring-[#d1974f]"
                  />
                </label>

                {isAdmin && (
                  <CategorySelector
                    categories={categories}
                    selectedCategoryId={methods.watch("category_id")}
                    onCategoryChange={handleCategoryChange}
                    isCreatingCategory={isCreatingCategory}
                    newCategoryName={newCategoryName}
                    onNewCategoryNameChange={setNewCategoryName}
                    onCreateCategory={handleCreateCategory}
                    onCancelCreate={() => setIsCreatingCategory(false)}
                    createNewValue={CREATE_NEW_CATEGORY_VALUE}
                  />
                )}
              </div>

              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#344054]">Description</span>
                <FormInput
                  name="description"
                  placeholder="Enter description"
                  className="h-11 rounded-md border-[#ddd5cb] bg-[#fffdfb] text-sm shadow-none placeholder:text-[#9aa4b2] focus-visible:ring-[#d1974f]"
                />
              </label>

              {isAdmin && (
                <SelectField
                  ariaLabel="Select type"
                  label="Type"
                  options={TYPE_OPTIONS}
                  value={effectiveType}
                  onChange={value => methods.setValue("type", value)}
                  hint={
                    businessReport
                      ? "Type can only be changed before any outcomes are generated from this template."
                      : null
                  }
                />
              )}

              {isAdmin && isAuthoring && (
                <SelectField
                  ariaLabel="Visibility"
                  label="Visibility"
                  options={VISIBILITY_OPTIONS}
                  value={effectiveVisibility}
                  onChange={value => methods.setValue("visibility", value)}
                  hint="Private templates are visible to their owner and library admins only."
                />
              )}

              {isAuthoring && (
                <AuthoringChecksEditor
                  checks={checks}
                  onChange={setChecks}
                  onFileName={name => {
                    if (!methods.getValues("name")) methods.setValue("name", name);
                  }}
                  disabled={methods.formState.isSubmitting}
                />
              )}

              {!businessReport && !isAuthoring && (
                <label className="flex min-h-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#d5c6b4] bg-[#fffdfb] px-6 py-8 text-center transition hover:border-[#c88b43] hover:bg-white">
                  <UploadCloud className="mb-3 h-8 w-8 text-[#c88b43]" />
                  <span className="text-sm font-semibold text-[#07142d]">Upload spreadsheet template</span>
                  <span className="mt-1 text-xs font-medium text-[#687386]">XLSX files only</span>
                  <FormInput
                    name="file"
                    type="file"
                    className="sr-only"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                </label>
              )}
            </div>

            <DialogFooter className="border-t border-[#ebe5de] bg-[#fffdfb] px-7 py-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="h-10 rounded-md border-[#ddd5cb] bg-white px-4 text-sm font-semibold text-[#344054] shadow-none hover:bg-[#f7f2ec]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={methods.formState.isSubmitting}
                className="h-10 rounded-md bg-[#07142d] px-5 text-sm font-semibold text-white shadow-none hover:bg-[#111b35]"
              >
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
